import type { AgentAnnotationShape } from "@/lib/agent-annotation";
import { parseDesiredContentSize } from "@/lib/agent-presentation-frame-size";
import { normalizeSequenceScreenshotTargets } from "@/lib/agent-presentation-screenshot-targets";
import type { AgentPresentationStep } from "@/lib/agent-presentation-types";
import { validEditExpectation } from "@/lib/desktop-edit-expectation";
import { validAppCommand } from "@/lib/desktop-app-command";

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function finite(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function point(value: unknown, normalized = false): boolean {
  const candidate = record(value);
  const x = finite(candidate?.x);
  const y = finite(candidate?.y);
  return Boolean(
    x !== undefined &&
    y !== undefined &&
    (!normalized || (x >= 0 && x <= 1 && y >= 0 && y <= 1)),
  );
}

function points(value: unknown, normalized = false): boolean {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    value.length <= 200 &&
    value.every((item) => point(item, normalized))
  );
}

function pathTarget(value: unknown): boolean {
  const target = record(value);
  return Boolean(target && points(target.points));
}

function hasExactlyOne(step: Record<string, unknown>, keys: string[]): boolean {
  return keys.filter((key) => step[key] !== undefined).length === 1;
}

function validCommon(step: Record<string, unknown>): boolean {
  if (!text(step.step_id)) return false;
  if (
    step.user_action !== undefined &&
    (!["point", "highlight", "draw"].includes(String(step.kind)) ||
      !["click", "type", "enter", "drag"].includes(String(step.user_action)))
  )
    return false;
  if (
    step.user_action === "drag" &&
    (step.kind !== "draw" ||
      !["arrow", "path", "polyline"].includes(String(step.shape)) ||
      !points(step.path, true))
  )
    return false;
  if (step.bubble !== undefined && !text(step.bubble)) return false;
  if (step.narration !== undefined && !text(step.narration)) return false;
  if (
    step.speech_role !== undefined &&
    !["answer", "progress"].includes(String(step.speech_role))
  )
    return false;
  if (
    step.callout_role !== undefined &&
    (!["highlight", "draw"].includes(String(step.kind)) ||
      !["context", "answer"].includes(String(step.callout_role)) ||
      !text(step.callout))
  )
    return false;
  return (
    step.pace === undefined ||
    step.pace === "quick" ||
    step.pace === "normal" ||
    step.pace === "slow"
  );
}

/**
 * A key goes to a window's keyboard, never to a point. It is aimed at one
 * thing - an element to focus, or a window whose caret it goes to - and its
 * point is the one Denker filled in for drawing, so nothing else may place it.
 */
const POINT_FIELDS = [
  "global_point",
  "capture_point",
  "screenshot_point",
  "frame_target",
];

/**
 * A key is aimed (Windows): at an element or a window's caret, drawn at the
 * point enrichment filled in. Or it is unaimed (macOS `desktop_interact`): it goes
 * to the focused application and has nothing to draw at.
 */
function validKeyStep(step: Record<string, unknown>): boolean {
  const keys = text(step.keys);
  const repeat = step.repeat;
  const window = step.window_id;
  if (
    !(keys && keys.length <= 120) ||
    !(
      repeat === undefined ||
      (Number.isInteger(repeat) &&
        (repeat as number) >= 1 &&
        (repeat as number) <= 50)
    )
  ) {
    return false;
  }
  // A system-wide shortcut names nothing and is drawn where Denker put it.
  if (step.scope !== undefined) {
    return (
      step.scope === "system" &&
      step.semantic_target === undefined &&
      window === undefined &&
      hasExactlyOne(step, POINT_FIELDS) &&
      step.global_point !== undefined
    );
  }
  if (step.semantic_target === undefined && window === undefined) {
    return POINT_FIELDS.every((field) => step[field] === undefined);
  }
  return (
    (step.semantic_target === undefined) !== (window === undefined) &&
    (window === undefined || Boolean(text(window))) &&
    hasExactlyOne(step, POINT_FIELDS) &&
    step.global_point !== undefined
  );
}

function validRectStep(
  step: Record<string, unknown>,
  shapes: ReadonlySet<string>,
): boolean {
  const shape = text(step.shape);
  if (!shape || !shapes.has(shape)) return false;
  if (step.callout !== undefined && !text(step.callout)) return false;
  if (
    !hasExactlyOne(step, [
      "global_rect",
      "capture_rect",
      "screenshot_rect",
      "frame_target",
    ])
  ) {
    return false;
  }
  const pathShape =
    shape === "path" || shape === "polyline" || shape === "arrow";
  return pathShape ? points(step.path, true) : step.path === undefined;
}

const highlightShapes = new Set<AgentAnnotationShape>([
  "rectangle",
  "rounded_rectangle",
  "square",
  "circle",
  "ellipse",
  "marker",
  "underline",
  "path",
  "polyline",
]);
const drawShapes = new Set([
  "rectangle",
  "rounded_rectangle",
  "circle",
  "ellipse",
  "path",
  "polyline",
  "arrow",
]);

function validStep(item: unknown): boolean {
  const step = record(item);
  const kind = text(step?.kind);
  if (!step || !kind || !validCommon(step)) return false;
  if (
    step.app_command !== undefined &&
    (kind !== "click" ||
      !validAppCommand(step.app_command) ||
      step.semantic_target !== undefined ||
      step.window_id !== undefined ||
      step.background !== undefined ||
      (step.button ?? "left") !== "left" ||
      (step.click_count ?? 1) !== 1 ||
      step.global_point === undefined)
  )
    return false;
  if (
    step.edit_expectation !== undefined &&
    (kind !== "paste" ||
      !validEditExpectation(step.edit_expectation) ||
      !record(step.semantic_target) ||
      step.replace === true ||
      step.background === true ||
      step.window_id !== undefined)
  )
    return false;
  if (
    kind === "point" ||
    kind === "click" ||
    kind === "hover" ||
    kind === "scroll" ||
    kind === "paste"
  ) {
    if (!hasExactlyOne(step, POINT_FIELDS)) {
      return false;
    }
    if (kind === "scroll") {
      return (
        ["up", "down", "left", "right"].includes(String(step.direction)) &&
        ["line", "page"].includes(String(step.amount))
      );
    }
    if (kind === "paste") {
      const pasteText = text(step.text);
      return (
        Boolean(pasteText && pasteText.length <= 10_000) &&
        (step.replace === undefined || typeof step.replace === "boolean") &&
        // A field or a window's caret, never both.
        (step.window_id === undefined ||
          (Boolean(text(step.window_id)) && step.semantic_target === undefined))
      );
    }
    if (kind === "hover") {
      // The companion's bounds (`desktop_pointer_hover.rs`).
      const dwell = step.dwell_ms;
      return (
        dwell === undefined ||
        (Number.isInteger(dwell) &&
          (dwell as number) >= 100 &&
          (dwell as number) <= 1_500)
      );
    }
    if (kind === "click") {
      // A semantic target with a plain left single click takes the AX press
      // path; any other button or count clicks visually at live geometry
      // filled by presentation enrichment.
      return (
        (step.button === undefined ||
          step.button === "left" ||
          step.button === "right") &&
        (step.click_count === undefined ||
          step.click_count === 1 ||
          step.click_count === 2)
      );
    }
    return true;
  }
  if (kind === "key") return validKeyStep(step);
  if (kind === "highlight") return validRectStep(step, highlightShapes);
  if (kind === "draw") return validRectStep(step, drawShapes);
  if (kind === "type") {
    const typeText = text(step.text);
    return Boolean(typeText && typeText.length <= 10_000);
  }
  if (kind === "drag") {
    if (
      !hasExactlyOne(step, ["global_path", "capture_path", "screenshot_path"])
    ) {
      return false;
    }
    return pathTarget(
      step.global_path ?? step.capture_path ?? step.screenshot_path,
    );
  }
  if (kind === "frame") {
    return (
      parseDesiredContentSize(step.desired_size) !== null &&
      Array.isArray(step.frame_ids) &&
      step.frame_ids.length > 0 &&
      step.frame_ids.every((id) => typeof id === "string")
    );
  }
  if (kind === "pause") return finite(step.duration_ms) !== undefined;
  if (kind === "clear") return true;
  return false;
}

export function parsePresentationSteps(
  value: unknown,
): AgentPresentationStep[] | null {
  if (!Array.isArray(value) || value.length === 0 || !value.every(validStep)) {
    return null;
  }
  return normalizeSequenceScreenshotTargets(value);
}
